/**
 * persist.mjs — the desk and the cabinet, and what is on each when the lights go out.
 *
 * FOUNDER, 2026-08-14: "ch6 next".
 *
 * ── THE CHAPTER'S OWN ONE LINE ────────────────────────────────────────────────────────────────
 * §1: "Hold this one line: memory is the desk, storage is the cabinet. Closing an app clears the
 * desk. The cabinet is untouched." So the lab is two containers and one event, and every word on
 * screen uses his two nouns rather than RAM and SSD — the picture is the teaching, and swapping in
 * the hardware words would be swapping the lesson for a fact.
 *
 * §2 supplies the drama and the mechanism: "well-built apps push half-typed drafts into storage
 * every few seconds… if your phone ran out of memory while you were away, the app might have been
 * kicked out of memory entirely, and your draft is gone."
 *
 * ── THE THREE EVENTS ARE THE SAME EVENT, AND THAT IS THE POINT ────────────────────────────────
 * §1 lists them in one breath as one thing: "the moment power is removed (you close the app, the
 * laptop goes to sleep, the phone is rebooted), everything in memory is gone." Closing an app feels
 * deliberate, a power cut feels like violence, the operating system reclaiming memory feels like a
 * betrayal — and the machine draws no distinction whatever between them. Modelling them as three
 * different mechanisms would invent a difference his text denies. They differ only in how much
 * warning the user had, which is a fact about people, not about memory.
 */

/** His two nouns. Held here so no surface can quietly rename them to RAM and disk. */
export const DESK = 'the desk';
export const CABINET = 'the cabinet';
export const ONE_LINE = 'memory is the desk, storage is the cabinet. Closing an app clears the desk. The cabinet is untouched.';
export const SOURCE_1 = 'Code101 module 06 §1 "Two kinds of memory — fast and forgetful vs slow and permanent"';
export const SOURCE_2 = 'Code101 module 06 §2 "Why apps “forget”"';

export const PROPERTIES = Object.freeze({
  memory: { name: 'Memory', picture: 'the desk', speed: 'nanoseconds to read or write', keeps: 'volatile',
    quote: 'Memory is fast — nanoseconds to read or write — but it is volatile: the moment power is removed (you close the app, the laptop goes to sleep, the phone is rebooted), everything in memory is gone.' },
  storage: { name: 'Storage', picture: 'the filing cabinet', speed: 'milliseconds to read or write, sometimes much more', keeps: 'persistent',
    quote: 'Storage is much slower than memory — milliseconds to read or write, sometimes much more — but it is persistent: when you turn off the phone and turn it back on three days later, everything in storage is still there.' },
});

/**
 * The three interruptions, quoted from §1's own parenthetical. `clearsMemory` is true for every one
 * of them because that is what the module says — see the header. What differs is only the label.
 */
export const EVENTS = Object.freeze([
  { id: 'close', label: 'You close the app', clearsMemory: true, feels: 'deliberate' },
  { id: 'sleep', label: 'The laptop goes to sleep', clearsMemory: true, feels: 'harmless' },
  { id: 'reboot', label: 'The phone is rebooted', clearsMemory: true, feels: 'like violence' },
  /* §2's specific accident, which is the one that produces "I lost my message". */
  { id: 'reclaim', label: 'Another app is hungry and the OS reclaims the memory', clearsMemory: true, feels: 'like a betrayal',
    quote: 'if your phone ran out of memory while you were away (because another app was hungry), the app might have been kicked out of memory entirely, and your draft is gone.' },
]);

/** §2: "well-built apps push half-typed drafts into storage every few seconds." */
export const AUTOSAVE_MS = 3000;
export const WELL_BUILT = 'Well-built software writes to storage often — every keystroke, every few seconds, at every meaningful step.';

export const empty = () => ({ desk: '', cabinet: '', lastSavedLen: 0 });

/** Typing touches the desk and NOTHING else. The whole drama depends on this staying true. */
export const type = (s, text) => ({ ...s, desk: String(text) });

/** A save copies the desk to the cabinet. Only ever in this direction. */
export const save = (s) => ({ ...s, cabinet: s.desk, lastSavedLen: s.desk.length });

/**
 * The interruption. Clears the desk; the cabinet is untouched — his sentence, as code.
 * Returns what was LOST so the surface can show the gap rather than merely the survivor: a learner
 * who sees only what is left has to work out what is missing, and the missing part is the lesson.
 */
export function interrupt(s) {
  const survived = s.cabinet;
  const lost = s.desk.length > survived.length && s.desk.startsWith(survived)
    ? s.desk.slice(survived.length)
    : (s.desk === survived ? '' : s.desk);
  return { state: { ...s, desk: '' }, survived, lost, lostChars: lost.length };
}

/** Reopening the app reads the cabinet back onto the desk — the only way anything returns. */
export const reopen = (s) => ({ ...s, desk: s.cabinet });

/** §5, named and NOT modelled. Declared here so the surface can say so rather than imply coverage. */
export const NOT_MODELLED = Object.freeze({
  cache: 'Between memory and the slowest storage, software puts caches: small copies of slow things, kept close, so they do not have to be fetched again.',
  why: 'Caches are §5 of this module and are a third container with a problem of their own — staleness. Showing two containers honestly is this lab; a third would make it a diagram of the module rather than a demonstration of its one line.',
  source: 'Code101 module 06 §5 "The cache — copies in the middle"',
});
